import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, Box, Typography, IconButton, Button, Stepper, Step, StepLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';

import WebhookScopeModal from './WebhookScopeModal';
import WebhookConfigureModal from './WebhookConfigureModal';
import WebhookSuccessModal from './WebhookSuccessModal';
import { useMutateWebhookHandler } from '../dataHandlers/mutateWebhookHandler';
import { useGetBusinessProfilesHandler } from '../../../dataHandlers/getBusinessProfilesHandler';

const initialFormState = {
  id: '',
  businessProfiles: [],
  events: [],
  name: '',
  url: '',
  description: '',
  isExcludedFromGlobalConfig: true,
  signingSecret: '',
  isGlobal: false,
};

const WebhookForm = ({ open, onClose, mode = 'create', initialData, isGlobal = false }) => {
  const dispatch = useDispatch();

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(initialFormState);

  const { profiles, totalProfiles, isLoading } = useGetBusinessProfilesHandler({});

  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && initialData) {
      setFormData({
        id: initialData.id,
        businessProfiles: initialData.profile_data ? initialData.profile_data.map((p) => p.profile_id) : [],
        events: initialData.event_types || [],
        name: initialData.name || '',
        url: initialData.url || '',
        description: initialData.description || '',
        isExcludedFromGlobalConfig: initialData.is_excluded_from_global_config ?? true,
        signingSecret: '',
        isGlobal: initialData.is_global ?? false,
      });
    } else {
      setFormData(initialFormState);
    }

    setStep(0);
  }, [open, mode, initialData]);

  const normalize = (data) => ({
    name: data.name?.trim() || '',
    url: data.url?.trim() || '',
    description: data.description?.trim() || '',
    events: [...(data.events || [])].sort((a, b) => String(a).localeCompare(String(b), 'en')),
    businessProfiles: [...(data.businessProfiles || [])].sort((a, b) => String(a).localeCompare(String(b), 'en')),
    isExcludedFromGlobalConfig: data.isExcludedFromGlobalConfig ?? true,
    isGlobal: data.isGlobal ?? false,
  });

  const initialSnapshot = useMemo(() => {
    if (mode !== 'edit' || !initialData) return null;

    return normalize({
      name: initialData.name,
      url: initialData.url,
      description: initialData.description,
      events: initialData.event_types || [],
      businessProfiles: initialData.profile_data ? initialData.profile_data.map((p) => p.profile_id) : [],
      isExcludedFromGlobalConfig: initialData.is_excluded_from_global_config ?? true,
      isGlobal: initialData.is_global ?? false,
    });
  }, [mode, initialData]);

  const currentSnapshot = useMemo(() => normalize(formData), [formData]);

  const hasChanges = useMemo(() => {
    if (mode !== 'edit' || !initialSnapshot) return true;
    return JSON.stringify(initialSnapshot) !== JSON.stringify(currentSnapshot);
  }, [mode, initialSnapshot, currentSnapshot]);

  const { mutate, isPending } = useMutateWebhookHandler({
    onSuccess: (_, data) => {
      if (mode === 'edit') {
        onClose();
      } else {
        setFormData((prev) => ({
          ...prev,
          signingSecret: data.secret,
        }));
        setStep(2);
      }
    },
  });

  const isStep1Valid = (isGlobal || formData.businessProfiles.length > 0) && formData.events.length > 0;

  const isValidWebhookUrl = (value) => {
    if (!value) return false;
    try {
      const parsed = new URL(value);
      if (parsed.protocol !== 'https:') return false;
      if (!parsed.hostname.includes('.') || parsed.hostname.startsWith('.')) return false;
      return true;
    } catch {
      return false;
    }
  };

  const isStep2Valid = formData.name.trim() !== '' && isValidWebhookUrl(formData.url.trim());

  const submit = () => {
    if (mode === 'edit' && !hasChanges) {
      dispatch(
        showSnackbar({
          message: 'No changes detected',
          severity: 'info',
        }),
      );
      return;
    }

    const selectedProfileData = profiles
      .filter((p) => formData.businessProfiles.includes(p.profile_id))
      .map((p) => ({
        profile_id: p.profile_id,
        tin: p.tin,
      }));

    const basePayload = {
      name: formData.name.trim(),
      url: formData.url.trim(),
      description: formData.description.trim(),
      profile_data: selectedProfileData,
      is_global: isGlobal,
      event_types: formData.events,
    };

    if (!isGlobal) {
      basePayload.is_excluded_from_global_config = formData.isExcludedFromGlobalConfig;
    }

    const payload = mode === 'edit' ? { id: formData.id, ...basePayload } : basePayload;

    mutate({ isEdit: mode === 'edit', payload });
  };

  return (
    <Dialog
      open={open}
      onClose={step === 2 ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: '#1e1e1e',
          color: '#fff',
          borderRadius: 2,
        },
      }}
    >
      <Box p={3} display="flex" justifyContent="space-between">
        <Typography fontWeight={900} fontSize={18}>
          Configure Webhook Destination
        </Typography>

        {step !== 2 && (
          <IconButton onClick={onClose}>
            <CloseIcon sx={{ color: '#9ca3af' }} />
          </IconButton>
        )}
      </Box>

      <Box>
        <Stepper activeStep={step} sx={{ justifyContent: 'flex-start', width: '100%', px: 23 }}>
          {['step1', 'step2', 'step3'].map((label) => (
            <Step key={label}>
              <StepLabel
                StepIconProps={{
                  sx: { fontSize: 30 },
                }}
              />
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box p={3}>
        {step === 0 && (
          <WebhookScopeModal
            formData={formData}
            setFormData={setFormData}
            profiles={profiles}
            isLoading={isLoading}
            totalProfiles={totalProfiles}
            isGlobal={isGlobal}
          />
        )}

        {step === 1 && <WebhookConfigureModal formData={formData} setFormData={setFormData} isGlobal={isGlobal} />}

        {step === 2 && <WebhookSuccessModal formData={formData} isLoading={isPending} />}
      </Box>

      <Box p={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          {step === 1 && (
            <Button variant="outlined" onClick={() => setStep(0)}>
              {'< Back'}
            </Button>
          )}
        </Box>

        <Box>
          {step === 0 && (
            <Button
              variant="contained"
              disabled={!isStep1Valid}
              onClick={() => setStep(1)}
              sx={{ background: '#6366f1' }}
            >
              {'Next >'}
            </Button>
          )}

          {step === 1 && (
            <Button
              variant="contained"
              disabled={!isStep2Valid || isPending}
              onClick={submit}
              sx={{ background: '#6366f1' }}
            >
              {isPending ? 'Saving…' : mode === 'edit' ? 'Save Webhook >' : 'Create Webhook >'}
            </Button>
          )}

          {step === 2 && (
            <Button variant="contained" onClick={onClose} sx={{ background: '#6366f1' }}>
              Done
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default WebhookForm;
